import classNames from "classnames";
import { AppKind } from "common/components/app-kind";
import { getLocalizedString } from "common/components/localization";
import type { TextEntrySize } from "common/generator/components/text-entry-types";
import { browserMightBeOniOS } from "lib/browser-detect";
import type { GlideFC } from "lib/glide-fc";
import React from "react";
import tw from "twin.macro";
import { css } from "webapp/styled-components";
import type { FieldType } from "../text-field/text-field-view-types";

interface Props {
    placeholder?: string;
    dataType?: FieldType;
    isEnabled: boolean;
    value: string;
    error?: string;
    onChange?: (newVal: string) => void;
    isRequired?: boolean;
    minimumChars?: number;
    maximumChars?: number;
    size?: TextEntrySize;
    title?: string;
    prefix?: string;
    suffix?: string;
}

export const WireField: GlideFC<Props> = p => {
    const { isEnabled, value, dataType, onChange, placeholder, title, isRequired, error, size, maximumChars } = p;

    const [innerValue, setInnerValue] = React.useState(value);

    const onValueChange = (newValue: string) => {
        setInnerValue(newValue);
        onChange?.(newValue);
    };

    React.useEffect(() => {
        setInnerValue(value);
    }, [value]);

    return (
        <label tw="flex flex-col text-text-dark">
            <div tw="mb-2 text-sm font-semibold flex justify-between items-baseline">
                <h3
                    data-testid="wf-title"
                    tw="flex-shrink text-text-contextual-dark overflow-hidden overflow-ellipsis whitespace-nowrap">
                    {title}
                </h3>
                {isRequired && (
                    <div data-testid="wf-required" tw="flex-shrink-0 ml-2 text-xs text-text-xpale font-normal">
                        {getLocalizedString("required", AppKind.Page)}
                    </div>
                )}
            </div>
            {dataType === "text" && (
                <WireGrowingTextArea
                    data-testid="wf-input"
                    className={size}
                    css={css`
                        &.medium {
                            min-height: 76px;
                        }

                        &.large {
                            min-height: 152px;
                        }
                    `}
                    tw="px-3 py-2 bg-bg-front shadow-sm hover:shadow-sm-xdark transition-shadow focus:(ring-accent
                        ring-2) rounded-lg"
                    value={innerValue}
                    disabled={!isEnabled}
                    placeholder={placeholder}
                    onChange={e => onValueChange?.(e.target.value)}
                />
            )}
            {dataType !== "text" && (
                <input
                    data-testid="wf-input"
                    tw="disabled:(opacity-50 pointer-events-none) px-3 py-2 bg-bg-front shadow-sm hover:shadow-sm-xdark
                        transition-shadow focus:(ring-accent ring-2) rounded-lg"
                    type={dataType}
                    value={innerValue}
                    disabled={!isEnabled}
                    placeholder={placeholder}
                    onChange={e => onValueChange?.(e.target.value)}
                />
            )}
            <div tw="flex-shrink-0 mt-1 text-xs flex justify-between">
                <div tw="text-text-danger">{error}</div>
                {maximumChars !== undefined && (
                    <div
                        css={css`
                            &.error {
                                ${tw`text-text-danger`};
                            }
                        `}
                        tw="text-text-xpale"
                        className={classNames(value.length > maximumChars && "error")}>
                        {`${value.length}/${maximumChars}`}
                    </div>
                )}
            </div>
        </label>
    );
};

interface WireGrowingEntryProps
    extends React.DetailedHTMLProps<React.TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement> {}

const WireGrowingTextArea = React.forwardRef<HTMLTextAreaElement, WireGrowingEntryProps>((p, ref) => {
    const { placeholder, value, className, ...rest } = p;

    return (
        <div tw="relative all-child:(p-0 m-0 color[inherit] font-size[inherit] font-weight[inherit])">
            <div style={{ padding: browserMightBeOniOS ? "0 3px" : undefined }} tw="w-full invisible">
                <div className={className} tw="whitespace-pre-wrap break-words">
                    {(value ?? placeholder ?? "") + "\n"}
                </div>
            </div>
            <textarea
                className={className}
                tw="inset-0 absolute rounded-none resize-none whitespace-pre-wrap w-full h-full overflow-hidden
                    border-0 bg-transparent disabled:(opacity-50 pointer-events-none)"
                ref={ref}
                placeholder={placeholder}
                value={value ?? ""}
                {...rest}
            />
        </div>
    );
});
